import { isEqual } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { UserGeneratedInputType } from 'types';
import { useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import QuestionEditor from 'views/Widgets/Inputs/QuestionEditor';
import { normalizeForComparison } from 'views/Widgets/Inputs/QuestionEditorCommon';
import {
  ALLOWED_QUESTION_TYPES,
  type CustomSurveyQuestionData,
  EXCLUDE_SPECIAL_QUESTIONS,
} from './consts';

// Stable element (referenced in QuestionEditor's memo deps, so it must not be
// recreated on every render).
const SAVE_TO_ENABLE_TRANSLATIONS_MESSAGE = (
  <FormattedMessage
    id="app.views.widgets.inputs.input_translation_editor.input_translation_editor.tooltip.save_before_translating"
    defaultMessage="Save the question to enable translations"
  />
);

const toUserGeneratedInput = (
  questionData: CustomSurveyQuestionData
): UserGeneratedInputType => {
  const {
    canBeAnonymous,
    canBePrivate,
    canAllowCalibration,
    attributes,
    format,
    id,
    ...input
  } = questionData.question;

  return input;
};

// Compares only the fields toUserGeneratedInput maps (i.e. actual question
// content). Excludes `name`: it's an internal id not exposed in this editor
// (hideAdvancedFeatures hides the ID field) and gets regenerated by
// QuestionEditor on any type change, even a round-trip back to the original type.
const isUnchanged = (
  newQuestion: UserGeneratedInputType,
  existingData: CustomSurveyQuestionData
): boolean => {
  const pickComparableFields = (data: UserGeneratedInputType) => ({
    type: data.type,
    label: data.label,
    helperText: data.helperText,
    placeholder: data.placeholder,
    section: data.section,
    required: data.required,
    anonymous: data.anonymous,
    hide_from_recipient: data.hide_from_recipient,
    allow_calibration: data.allow_calibration,
    factor: data.factor,
    filters: data.filters,
    scale: data.scale,
    objects: data.objects,
    options: data.options,
    minSelections: data.minSelections,
    maxSelections: data.maxSelections,
    maxLength: data.maxLength,
  });

  return isEqual(
    normalizeForComparison(pickComparableFields(newQuestion)),
    normalizeForComparison(
      pickComparableFields(toUserGeneratedInput(existingData))
    )
  );
};

// Edits POST a new version (same uuid, version + 1) to preserve history.
const toApiPayload = (
  userGeneratedInput: UserGeneratedInputType,
  existingData?: CustomSurveyQuestionData | null
): CustomSurveyQuestionData => {
  const payload: CustomSurveyQuestionData = {
    question: userGeneratedInput,
    status: existingData?.status ?? 'ACTIVE',
  };
  if (existingData?.uuid) {
    payload.uuid = existingData.uuid;
    payload.version = (existingData.version ?? 0) + 1;
  }
  return payload;
};

interface Props {
  isOpen: boolean;
  toggle: () => void;
  editingQuestion: CustomSurveyQuestionData | null;
  onSaved: (data: CustomSurveyQuestionData, isEdit: boolean) => void;
}

const CustomSurveyQuestionEditorModal: FC<Props> = ({
  isOpen,
  toggle,
  editingQuestion,
  onSaved,
}) => {
  const { formatMessage } = useIntl();
  const proxyParameters = useProxyParameters();

  // QuestionEditor resets its in-progress edits whenever this reference
  // changes (it treats that as the question being replaced externally), so
  // it must stay stable across re-renders triggered by e.g. handleQuestionChange.
  const question = useMemo(
    () => (editingQuestion ? toUserGeneratedInput(editingQuestion) : undefined),
    [editingQuestion]
  );

  // Editing a saved question and then changing any field means the next save
  // creates a new version (uuid + version). Translations are keyed by that
  // version, so disable translating fields until the edits are saved.
  const [translationsDisabled, setTranslationsDisabled] = useState(false);
  // editingQuestion changes reference whenever a (freshly-saved or newly
  // selected) question is loaded into the form, at which point there are no
  // unsaved edits yet, so translations should be re-enabled.
  useEffect(() => {
    setTranslationsDisabled(false);
  }, [editingQuestion]);
  const handleQuestionChange = useCallback(
    (liveQuestion: UserGeneratedInputType) => {
      setTranslationsDisabled(
        !!editingQuestion && !isUnchanged(liveQuestion, editingQuestion)
      );
    },
    [editingQuestion]
  );

  // Async submit via submitPromise so the modal can stay open on success
  // (keepOpenOnSuccess) while the submit button reflects the real save state:
  // the spinner shows for the duration of the POST and clears when `done` is
  // called. `done(data)` resolves as success; `done(null, error, msg)` as error.
  const handleSave = useCallback(
    (
      newQuestion: UserGeneratedInputType,
      done: (data?: any, error?: any, hardErrorMessage?: string | null) => void
    ) => {
      const payload = toApiPayload(newQuestion, editingQuestion);
      const isEditing = !!editingQuestion?.uuid;

      ConfirmAPI.sendRequestToConfirm(
        'POST',
        'custom-survey/questions/admin',
        { ...proxyParameters, ...payload },
        (data, error, hardErrorMessage) => {
          if (error || hardErrorMessage) {
            toast.error(
              formatMessage({
                id: 'app.views.administration.custom_surveys.questions.save_error',
                defaultMessage: 'Failed to save question.',
              })
            );
            done(null, error, hardErrorMessage);
            return;
          }
          onSaved(data, isEditing);
          done(data);
        }
      );
    },
    [proxyParameters, formatMessage, onSaved, editingQuestion]
  );

  // `callback` is required by QuestionEditor and still fires on success (with
  // keepOpenOnSuccess), but the save + parent notification are handled in
  // submitPromise above, so this is intentionally a no-op.
  const noop = useCallback(() => {}, []);

  return (
    <QuestionEditor
      isOpen={isOpen}
      toggle={toggle}
      question={question}
      callback={noop}
      submitPromise={handleSave}
      title={
        editingQuestion
          ? formatMessage({
              id: 'app.views.administration.custom_surveys.questions.editor.title.edit',
              defaultMessage: 'Edit question',
            })
          : formatMessage({
              id: 'app.views.administration.custom_surveys.questions.editor.title.create',
              defaultMessage: 'Create question',
            })
      }
      submitText={
        editingQuestion
          ? formatMessage({
              id: 'app.views.administration.custom_surveys.questions.editor.save',
              defaultMessage: 'Save question',
            })
          : formatMessage({
              id: 'app.views.administration.custom_surveys.questions.editor.create',
              defaultMessage: 'Create question',
            })
      }
      hideAdvancedFeatures={true}
      allowedQuestionTypes={ALLOWED_QUESTION_TYPES}
      disableTypeChange={!!editingQuestion}
      typeChangeDisabledMessage={formatMessage({
        id: 'app.views.administration.custom_surveys.questions.editor.type_change_disabled',
        defaultMessage: 'Question type cannot be changed after creation.',
      })}
      excludeSpecialQuestions={EXCLUDE_SPECIAL_QUESTIONS}
      triggerCallbackBeforeClose={true}
      keepOpenOnSuccess={!!editingQuestion}
      translationNamespace={
        editingQuestion
          ? `custom_survey_question_${editingQuestion.uuid}_v${editingQuestion.version}`
          : undefined
      }
      jsonPath="$"
      onChange={handleQuestionChange}
      translationDisabled={translationsDisabled}
      translationDisabledMessage={SAVE_TO_ENABLE_TRANSLATIONS_MESSAGE}
    />
  );
};

export default React.memo(CustomSurveyQuestionEditorModal);
